import {Button} from "@luciad/ria-sample-common/components/button/Button";
import {ButtonGroup} from "@luciad/ria-sample-common/components/ButtonGroup";
import {FormControl} from "@luciad/ria-sample-common/components/FormControl";
import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {IToastContext, useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import {Map} from "@luciad/ria/view/Map";
import React, {FormEvent, useRef} from "react";
import {openFileOrUrl} from "../DataFormatsUtil";
import {ConnectToURLPage} from "./ConnectToURLPage";
import {ModalPages, ModalWizard, WizardPageProps} from "./ModalWizard";
import "./OpenFileModal.css";
import {WizardPreviousNextButtons} from "./WizardPreviousNextButtons";

interface Props {
  show: boolean;
  onHide: () => void;
}

export const OpenFileModal = ({show, onHide}: Props) => {
  return <ModalWizard show={show} onHide={onHide} startPageKey="chooseType" pages={PAGES}/>;
}

type OpenFilePages = "chooseType" | "fromLocalFile" | "fromURL";

const PAGES: ModalPages<OpenFilePages> = {
  "chooseType": {
    title: "Open file(s)",
    render: (changePage) => {
      return (
          <ButtonGroup vertical={true}>
            <Button onClick={() => changePage("fromLocalFile")}>From local file(s)</Button>
            <Button onClick={() => changePage("fromURL")}>From URL</Button>
          </ButtonGroup>
      )
    }
  },
  "fromLocalFile": {
    title: "Open local file(s)",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
          <FromLocalFileForm changePage={changePage} previousPageKey={previousPageKey} resetWizard={resetWizard}/>
      )
    }
  },
  "fromURL": {
    title: "Open file from URL",
    render: (changePage, previousPageKey, resetWizard) => {
      return (
          <FromURLForm changePage={changePage} previousPageKey={previousPageKey} resetWizard={resetWizard}/>
      )
    }
  }
}

const FromLocalFileForm = ({changePage, previousPageKey, resetWizard}: WizardPageProps<OpenFilePages>) => {
  const {map} = useMapContext();
  const {showErrorToast} = useToastContext();
  // file inputs are always uncontrolled https://reactjs.org/docs/uncontrolled-components.html#the-file-input-tag
  const fileInput = useRef<HTMLInputElement>(undefined!);
  const handleFormSubmit = (ev?: FormEvent) => {
    if (ev) {
      ev.preventDefault();
    }
    const files = fileInput.current.files;
    if (files && files.length > 0) {
      for (const file of files) {
        openFileOrUrl(map, file).catch(error => {
          showErrorToast(error.message, error);
        });
      }
      resetWizard();
    }
  }

  return (
      <form onSubmit={handleFormSubmit} className="open-file-form">
        <div className="open-file-form-content">
          <FormControl>
            <label htmlFor="localFile">FILE</label>
            <input name="localFile" type="file" ref={fileInput} multiple={true}/>
          </FormControl>
        </div>
        <WizardPreviousNextButtons
            onPrevious={() => changePage(previousPageKey)}
            onNext={handleFormSubmit}
            nextLabel="Open"
        />
      </form>
  )
}

const FromURLForm = ({changePage, previousPageKey, resetWizard}: WizardPageProps<OpenFilePages>) => {

  const handleSubmit = (url: string, map: Map, toastContext: IToastContext) => {
    openFileOrUrl(map, url).catch(error => {
      toastContext.showErrorToast(error.message, error);
    });
    resetWizard();
  };

  return (
      <ConnectToURLPage onSubmit={handleSubmit}
                        onPrevious={() => changePage(previousPageKey)}
      />
  );
}